import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "professional_certification";

const ProfessionalCertification = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    awarding_year: null,
    certification: "",
    certification_association: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);

  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = data
      .map((rec) => ({
        ...rec,
        awarding_year: rec.awarding_year
          ? new Date(rec.awarding_year)
          : null,
      }))
      .sort((a, b) => {
        if (!a.awarding_year) return 1;
        if (!b.awarding_year) return -1;

        return (
          b.awarding_year.getTime() -
          a.awarding_year.getTime()
        );
      });

    setRecords(processed);
  } catch (err) {
    toast.error(err.message);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      awarding_year: formData.awarding_year
        ? format(formData.awarding_year, "yyyy-MM") + "-01"
        : null,
    };

    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success(
        recordId ? "Record updated successfully!" : "Record added successfully!"
      );

      setFormData({
        awarding_year: null,
        certification: "",
        certification_association: "",
        country: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleEdit = (rec) => {
    setFormData({
      awarding_year: rec.awarding_year ? new Date(rec.awarding_year) : null,
      certification: rec.certification || "",
      certification_association: rec.certification_association || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                {
                  method: "DELETE",
                  credentials: "include",
                }
              );
              if (!res.ok) throw new Error("Failed to delete");
              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: err.message,
                type: "error",
                autoClose: 3000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const inputStyle = {
    padding: "10px",
    marginTop: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    width: "100%",
    fontSize: "16px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Professional Certifications
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Awarding Year */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Awarding Year (mm/yyyy){" "}
              <span
                style={{
                  fontWeight: 300,
                  fontSize: "0.85em",
                  marginLeft: "6px",
                  color: "#666",
                }}
              >
                (start with latest certification)
              </span>
            </label>
            <DatePicker
              selected={formData.awarding_year}
              onChange={(date) =>
                setFormData({ ...formData, awarding_year: date })
              }
              dateFormat="MM/yyyy"
              showMonthYearPicker
              placeholderText="Select month/year"
              style={inputStyle}
            />
          </div>

          {/* Certification */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Certificate Name
            </label>
            <input
              type="text"
              name="certification"
              value={formData.certification}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          {/* Certification Association */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Awarding Entity (Association/Organization)
            </label>
            <input
              type="text"
              name="certification_association"
              value={formData.certification_association}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          {/* Country */}
          <div>
            <label style={{ fontWeight: "bold", marginTop: "10px" }}>
              Country
            </label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>Your Certifications</h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Awarding Year",
                    "Certification",
                    "Association",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.awarding_year
                        ? `${String(rec.awarding_year.getMonth() + 1).padStart(
                            2,
                            "0"
                          )}/${rec.awarding_year.getFullYear()}`
                        : ""}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.certification}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.certification_association}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalCertification;
